<template>
  <standard-dialog
    v-model="model"
    :title="editedItem.id ? $t('i_m') : $t('n_m')"
    :subtitle="editedItem?.id ? `ID: ${editedItem.id}` : ''"
    :max-width="1000"
    :fullscreen="device.isMobile"
    icon="mdi-food"
    @save="$emit('save')"
    :disabled="!valid"
  >
    <v-form v-model="valid" ref="form">
      <!-- LANGUAGE TABS -->
      <v-tabs v-model="activeLanguageTab" color="primary" class="mb-4">
        <v-tab v-for="lang in availableLanguages" :key="lang.code">
          {{ lang.text }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeLanguageTab">
        <v-tab-item v-for="lang in availableLanguages" :key="lang.code">
          <!-- BASIC INFO -->
          <food-basic-info
            :edited-item="editedItem"
            :categories="categories"
            :currency="$store.state.restaurant.settings?.price || ''"
            :read-only-category="readOnlyCategory"
            :language="lang.code"
          />

          <!-- SIZES & PRICES SECTION -->
          <v-card outlined v-if="editedItem.price && localSizes.length > 0">
            <v-card-subtitle class="d-flex align-center pb-2">
              <v-icon small color="primary" class="mr-2">mdi-resize</v-icon>
              <span class="font-weight-bold primary--text">{{ $t("meal_size") }}</span>
            </v-card-subtitle>
            <v-card-text>
              <v-row dense>
                <v-col
                  cols="12"
                  sm="6"
                  md="4"
                  v-for="(size, index) in localSizes"
                  :key="index"
                >
                  <v-card
                    outlined
                    class="size-mini-card"
                    :class="{ 'size-active': size.is_true }"
                  >
                    <v-card-text class="pa-3">
                      <div class="d-flex align-center justify-space-between mb-2">
                        <v-switch
                          v-model="size.is_true"
                          :label="size.size.name"
                          dense
                          hide-details
                          color="primary"
                          @change="updatePrice(size, index)"
                        ></v-switch>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div
                              v-bind="attrs"
                              v-on="on"
                            >
                            <v-checkbox
                              v-show="size.is_true"
                              v-model="size.default_size"
                              hide-details
                              @change="updatePrice(size, index)">
                            </v-checkbox>
                            </div>
                          </template>
                          <span>Osnovna cena koja se prikazuje na jelu</span>
                        </v-tooltip>

                      </div>

                      <v-expand-transition>
                        <div v-if="size.is_true">
                          <v-divider class="mb-3"></v-divider>
                          <text-field
                            v-model="size.price"
                            type="number"
                            :label="$t('price')"
                            :suffix="$store.state.restaurant.settings?.price || ''"
                            outlined
                            dense
                            hide-details
                            @input="updatePrice(size, index)"
                          ></text-field>
                          <text-field
                            v-model="size.last_price"
                            type="number"
                            label="Stara cena"
                            :suffix="$store.state.restaurant.settings?.price || ''"
                            outlined
                            dense
                            hide-details
                            @input="updatePrice(size, index)"
                          ></text-field>
                          <!-- <primary-button
                            block
                            small
                            class="mt-2"
                            @click="saveSizePrice(size)"
                            :loading="loading.savingPrice"
                            :disabled="!size.price"
                          >
                            <v-icon left x-small>mdi-content-save</v-icon>
                            {{ $t("save_price") }}
                          </primary-button> -->
                        </div>
                      </v-expand-transition>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- MENU TYPE -->
          <food-menu-type :edited-item="editedItem" :language="lang.code" />

          <!-- LABELS -->
          <food-labels :edited-item="editedItem" :language="lang.code" />

          <!-- COMPOSITION SECTION -->
          <food-grocery-composition :edited-item="editedItem" :language="lang.code" />

          <!-- SIDE DISHES SECTION -->
          <food-side-dishes
            :edited-item="editedItem"
            :side-dishes="sideDishes"
            :language="lang.code"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-form>

    <global-confirm-box ref="confirmBox"></global-confirm-box>
  </standard-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasValidation from "@/mixins/hasValidation";
import hasSnackbar from "@/mixins/hasSnackbar";
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import FoodGroceriesTable from "@/components/food/tables/FoodGroceriesTable.vue";
import FoodSideDishesTable from "@/components/food/tables/FoodSideDishesTable.vue";
import FoodBasicInfo from "./sections/FoodBasicInfo.vue";
import FoodMenuType from "./sections/FoodMenuType.vue";
import FoodLabels from "./sections/FoodLabels.vue";
import FoodGroceryComposition from "./sections/FoodGroceryComposition.vue";
import FoodSideDishes from "./sections/FoodSideDishes.vue";
import foodService from "@/services/food";
import axios from "../../../../plugins/axios";

export default {
  mixins: [hasModel, hasValidation, hasSnackbar],
  props: {
    editedItem: {
      type: Object,
      required: true,
    },
    categories: {
      type: Array,
      required: true,
    },
    additionalFoodNew: {
      type: Array,
      default: () => [],
    },
    sideDishes: {
      type: Array,
      default: () => [],
    },
    readOnlyCategory: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    StandardDialog,
    FoodGroceriesTable,
    FoodSideDishesTable,
    FoodBasicInfo,
    FoodMenuType,
    FoodLabels,
    FoodGroceryComposition,
    FoodSideDishes,
  },
  data: () => ({
    priceForAddon: "",
    activeLanguageTab: 0,
    localSizes: [],
    loading: {
      addingAdditionalFood: false,
      savingPrice: false,
      deletingPrice: false,
    },
  }),
  methods: {
    updatePrice(size, index) {
      this.editedItem.size_prices[index].price = size.price;
      this.editedItem.size_prices[index].is_true = size.is_true;
      this.editedItem.size_prices[index].last_price = size.last_price;

      let defaultSize = this.localSizes.filter((s) => s.is_true);
      defaultSize.forEach((s, i) => {

          s.default_size = i === index;
          if(this.editedItem.size_prices[i].id == size.id) {
            this.editedItem.size_prices[i].default_size = 1;
          }
          else {
            this.editedItem.size_prices[i].default_size = 0;
          }

      });
    },
    saveSizePrice(size) {
      this.priceForAddon = size;
      this.sacuvajCenu();
    },
    addChoiceNumber(number) {
      number.category_id = number.id;
      number.food_id = this.editedItem.id;
      foodService
        .addAdditionalFoodChoiceNumber(number)
        .then(({ data }) => {
          this.editedItem.additional_food = data;
          this.showSuccessSnackbar(this.$t("saved_successfully"));
        })
        .catch((error) => {
          this.showErrorSnackbar(error);
        });
    },

    sacuvajCenu() {
      this.loading.savingPrice = true;
      let data = {};
      data["podaci"] = this.priceForAddon;
      data["meal"] = this.editedItem;
      data["cena"] = this.new_price;
      foodService
        .addSizePrice(data)
        .then(({ data }) => {
          if (this.editedItem.selectedPrices.length) {
            this.editedItem.selectedPrices.filter((selected) => {
              if (selected.size_id == data.size_id && selected.meal_id == data.meal_id) {
                selected.price = data.price;
              }
            });
          } else {
            this.editedItem.selectedPrices.push(data);
          }
          this.editedItem.sizes.filter((size) => {
            if (size.id == data.size_id) {
              size.price_meal_id = data.id;
              size.price.push(data);
            }
          });
          this.$emit("getSizes");
          this.showSuccessSnackbar(this.$t("price_saved_successfully"));
        })
        .catch((error) => {
          console.error("Error saving price:", error);
          this.showErrorSnackbar(this.$t("error_saving_price"));
        })
        .finally(() => {
          this.loading.savingPrice = false;
        });
    },
    removeSizePrice(size) {
      this.$refs.confirmBox
        .open(this.$t("delete_portion"), this.$t("delete_portion_confirmation"), {
          color: "warning",
        })
        .then(() => {
          foodService
            .deleteSizePrice(size)
            .then(() => {
              this.$emit("getSizes");
              this.editedItem.sizes.filter((s, idn) => {
                if (s.price) {
                  s.price.filter((p, index) => {
                    if (p.id == this.editSizesPriceDelete.price_meal_id) {
                      s.is_true = false;
                      s.price_meal = "";
                    }
                  });
                }
              });
              this.showSuccessSnackbar(this.$t("deleted_successfully"));
            })
            .catch((error) => {
              this.showErrorSnackbar(error);
            });
        })
        .catch((err) => console.warn(err));
    },
    addToAdditionalCategories(index) {
      this.additionalFoodNew[index].category_id = this.additionalFoodNew[index].id;
      if (this.additionalFoodNew[index].active) {
        this.editedItem.additional_food.push(this.additionalFoodNew[index]);
        this.additionalFoodNew[index].food_id = this.editedItem.id;
        foodService
          .addOrUpdateAdditionalFoods(this.additionalFoodNew[index])
          .then(({ data }) => {
            this.editedItem.additional_food = data;
            this.showSuccessSnackbar(this.$t("saved_successfully"));
          })
          .catch((error) => {
            this.showErrorSnackbar(error);
          });
      } else {
        this.editedItem.additional_food.filter((item, i) => {
          if (item.category_id == this.additionalFoodNew[index].category_id) {
            item.remove = true;
            item.food_id = this.editedItem.id;
            foodService
              .addOrUpdateAdditionalFoods(item)
              .then(({ data }) => {
                this.editedItem.additional_food = data;
                this.showSuccessSnackbar(this.$t("saved_successfully"));
              })
              .catch((error) => {
                this.showErrorSnackbar(error);
              });
            this.editedItem.additional_food.splice(i, 1);
          }
        });
      }
    },
  },
  computed: {
    availableLanguages() {
      // Filter languages based on restaurant settings (same logic as LanguageSelector)
      const restaurant =
        this.$store.state.restaurant || this.$store.state.company?.restaurant;
      const restaurantLanguages = restaurant?.settings?.language || [];

      const def = [
        {
          code: "default",
          text: "default",
          locale: "default",
          flag: "🇷",
          script: "default",
        },
      ];

      if (restaurantLanguages.length === 0) {
        // If no restaurant languages configured, return all available languages
        let data = this.getAvailableLanguages();
        return def.concat(data);
      }

      // Filter available languages to only include those configured for the restaurant
      let data = this.getAvailableLanguages().filter((lang) =>
        restaurantLanguages.includes(lang.code)
      );
      return def.concat(data);
    },
    headers() {
      return [
        { text: this.$t("id_pr"), value: "id", sortable: true },
        { text: this.$t("image"), align: "start", value: "image_id" },
        { text: this.$t("title"), align: "start", value: "name" },
        { text: this.$t("price"), value: "price" },
        { text: this.$t("group"), value: "category" },
        { text: this.$t("normativ"), value: "groceries" },
        { text: this.$t("s_v"), value: "for_sale" },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          width: "13%",
        },
      ];
    },
  },
  watch: {
    "editedItem.size_prices": {
      immediate: true,

      handler(val) {
        if (!val) return;
        this.localSizes = val.map((size) => ({
          ...size,
          is_true: !!size.price,
          default_size: size.default_size || 0
        }));
      },
    },
    model(model) {
      if (!model) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>

<style scoped>
/* Card styling */
.v-card {
  border-radius: 8px;
}

.v-card.outlined {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

/* Size cards */
.size-mini-card {
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.size-mini-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.size-mini-card.size-active {
  border-color: var(--v-primary-base);
  background-color: rgba(var(--v-primary-base), 0.02);
}
</style>
